import { Link } from "react-router-dom";
import { Mail, MapPin, Facebook, Youtube } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="mt-20"
      style={{ backgroundColor: "var(--ser-footer)", color: "rgba(255,255,255,0.85)" }}
      data-testid="main-footer"
    >
      <div className="ser-container px-6 md:px-12 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="mb-4">
            <BrandLogo height={56} linkTo={null} padded />
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
            The Africa Chapter of the Society for Ecological Restoration — advancing
            science-based restoration across the continent.
          </p>
        </div>

        <div>
          <p
            className="text-[0.72rem] tracking-[0.18em] uppercase font-bold mb-4"
            style={{ color: "var(--ser-primary-light)", fontFamily: "Lato, sans-serif" }}
          >
            Navigate
          </p>
          <ul className="space-y-2">
            {[
              ["/", "Home"],
              ["/about", "About"],
              ["/events", "Events"],
              ["/membership", "Membership"],
              ["/contact", "Contact"],
            ].map(([href, label]) => (
              <li key={href}>
                <Link
                  to={href}
                  className="text-sm hover:text-white transition-colors"
                  style={{ color: "rgba(255,255,255,0.7)" }}
                  data-testid={`footer-link-${label.toLowerCase()}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p
            className="text-[0.72rem] tracking-[0.18em] uppercase font-bold mb-4"
            style={{ color: "var(--ser-primary-light)", fontFamily: "Lato, sans-serif" }}
          >
            Contact
          </p>
          <ul className="space-y-3 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>South Africa</span>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 shrink-0" />
              <a href="mailto:africa@ser.org" className="hover:text-white transition-colors">
                africa@ser.org
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p
            className="text-[0.72rem] tracking-[0.18em] uppercase font-bold mb-4"
            style={{ color: "var(--ser-primary-light)", fontFamily: "Lato, sans-serif" }}
          >
            Follow
          </p>
          <div className="flex flex-wrap gap-3" data-testid="footer-socials">
            <a
              href="https://www.facebook.com/SocietyEcologicalRestorationAfrica/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              title="Facebook"
              className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-white/10 transition"
              data-testid="social-facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://x.com/ser_africa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              title="X (Twitter)"
              className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-white/10 transition"
              data-testid="social-x"
            >
              <XIcon />
            </a>
            <a
              href="https://youtube.com/@serafrica?si=Jx_aZpJUaSAHQlLr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              title="YouTube"
              className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-white/10 transition"
              data-testid="social-youtube"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <a
              href="https://whatsapp.com/channel/0029VbAWyOP2Jl8JNSfye446"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp Channel"
              title="WhatsApp Channel"
              className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-white/10 transition"
              data-testid="social-whatsapp-channel"
            >
              <WhatsAppIcon />
            </a>
            <a
              href="https://chat.whatsapp.com/EuplkapFqm1FzOoAg50Zxn?s=cl&p=a&mlu=4&ilr=4"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="SER AF WhatsApp Group"
              title="SER AF WhatsApp Group"
              className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-white/10 transition"
              data-testid="social-whatsapp-group"
            >
              <WhatsAppGroupIcon />
            </a>
          </div>
          <p className="mt-6 text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
            A chapter of the{" "}
            <a href="https://www.ser.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              Society for Ecological Restoration
            </a>
          </p>
        </div>
      </div>

      <div
        className="border-t py-4 text-xs flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2"
        style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.55)" }}
      >
        <span>© {year} SER Africa. All rights reserved.</span>
        <a
          href="https://byalani.co.za"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 hover:text-white transition-colors"
          data-testid="footer-byalani-link"
          title="Powered by Byalani"
        >
          <img
            src="https://www.google.com/s2/favicons?domain=byalani.co.za&sz=64"
            alt=""
            aria-hidden="true"
            className="w-4 h-4"
            loading="lazy"
          />
          <span>Powered by Byalani</span>
        </a>
      </div>
    </footer>
  );
}


/* ── Custom brand glyphs (lucide has no WhatsApp / X) ────────────────────── */
function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488" />
    </svg>
  );
}

function WhatsAppGroupIcon() {
  // Small "people/group" glyph tinted like WhatsApp so users can tell the two apart at a glance.
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="10" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M17 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

